import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { formatDateTime } from "../../lib/format";

const KIND_META = {
    press:    { label: "Press / Demo", pill: "bg-avx-blue/12 text-avx-blue border-avx-blue/40" },
    courtesy: { label: "Courtesy",      pill: "bg-avx-success/12 text-avx-success border-avx-success/40" },
};

export default function AdminSpecialLicenses() {
    const [licenses, setLicenses] = useState([]);
    const [users, setUsers] = useState([]);
    const [products, setProducts] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [kindFilter, setKindFilter] = useState("all");
    const [form, setForm] = useState({
        user_id: "",
        product_id: "",
        kind: "press",
        days: 30,
        max_activations: 1,
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [copied, setCopied] = useState("");

    const load = () => {
        api.get("/admin/licenses", { params: { kind: "press,courtesy" } })
            .then(({ data }) => setLicenses(data || []))
            .catch((e) => toast.error(apiErrorMessage(e)));
    };
    useEffect(load, []);
    useEffect(() => {
        api.get("/admin/users", { params: userSearch ? { q: userSearch } : {} })
            .then(({ data }) => setUsers(data || []));
    }, [userSearch]);
    useEffect(() => {
        api.get("/admin/products").then(({ data }) => setProducts((data || []).filter((p) => p.status !== "draft")));
    }, []);

    const filtered = useMemo(() => {
        if (kindFilter === "all") return licenses;
        return licenses.filter((l) => l.kind === kindFilter);
    }, [licenses, kindFilter]);

    const selectedUser = users.find((u) => u.id === form.user_id);
    const selectedProduct = products.find((p) => p.id === form.product_id);

    const issue = async (e) => {
        e.preventDefault();
        if (!form.user_id || !form.product_id) {
            toast.error("Select a user and a product");
            return;
        }
        const days = Number(form.days);
        if (!Number.isFinite(days) || days < 1) {
            toast.error("Invalid duration");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/licenses/issue-special", {
                user_id: form.user_id,
                product_id: form.product_id,
                kind: form.kind,
                days,
                max_activations: Number(form.max_activations) || 1,
                notes: form.notes || undefined,
            });
            toast.success(`${KIND_META[data.kind]?.label || data.kind} license issued`);
            setForm({ ...form, notes: "" });
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const revoke = async (lic) => {
        if (!window.confirm(`Revoke license ${lic.key}? This action is irreversible.`)) return;
        try {
            await api.post(`/admin/licenses/${lic.id}/revoke`);
            toast.success("License revoked");
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    const extend = async (lic) => {
        const raw = window.prompt(`How many days to add to ${lic.key}?`, "30");
        const days = Number(raw);
        if (!Number.isFinite(days) || days < 1) return;
        try {
            const { data } = await api.post(`/admin/licenses/${lic.id}/extend`, { days });
            toast.success(`Extended — new expiration ${new Date(data.expires_at).toLocaleDateString()}`);
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    const copyKey = (key) => {
        navigator.clipboard.writeText(key);
        setCopied(key);
        setTimeout(() => setCopied(""), 1500);
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8" data-testid="admin-special-licenses-page">
                <div className="label-mono mb-3">/ Admin · Special licenses</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">
                    Press & <span className="text-avx-blue">Courtesy</span>
                </h1>
                <p className="text-avx-muted mt-2 max-w-2xl">
                    Issue licenses without an associated purchase for reviewers, artists, collaborators or promotional campaigns. They auto-expire when the duration ends.
                </p>
            </div>

            {/* ISSUE FORM */}
            <form onSubmit={issue} className="grid lg:grid-cols-2 gap-6 mb-12" data-testid="issue-special-form">
                <div className="panel p-6 space-y-4">
                    <div className="label-mono">/ User</div>
                    <Input
                        placeholder="Search by name or email…"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="bg-avx-bg border-avx-line h-11"
                        data-testid="special-user-search"
                    />
                    <div className="max-h-72 overflow-y-auto border border-avx-line rounded-sm divide-y divide-avx-line">
                        {users.length === 0 ? (
                            <div className="p-4 text-sm text-avx-muted">No results.</div>
                        ) : (
                            users.map((u) => (
                                <button
                                    type="button"
                                    key={u.id}
                                    onClick={() => setForm({ ...form, user_id: u.id })}
                                    data-testid={`special-user-${u.id}`}
                                    className={`w-full text-left px-3 py-2 hover:bg-avx-surface/60 transition-colors ${
                                        form.user_id === u.id ? "bg-avx-blue/10 border-l-2 border-avx-blue" : ""
                                    }`}
                                >
                                    <div className="text-sm">{u.name} <span className="label-mono ml-2">{u.role}</span></div>
                                    <div className="font-mono text-xs text-avx-muted">{u.email}</div>
                                </button>
                            ))
                        )}
                    </div>
                    {selectedUser && (
                        <div className="panel p-3 text-xs bg-avx-blue/5 border-avx-blue/40">
                            <span className="text-avx-muted">Selected:</span>{" "}
                            <span className="font-bold">{selectedUser.name}</span>
                            <span className="text-avx-muted"> · </span>
                            <span className="font-mono">{selectedUser.email}</span>
                        </div>
                    )}
                </div>

                <div className="panel p-6 space-y-4">
                    <div className="label-mono">/ License details</div>
                    <div>
                        <Label className="label-mono">Product</Label>
                        <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                            <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11" data-testid="special-product-select">
                                <SelectValue placeholder="Select plugin" />
                            </SelectTrigger>
                            <SelectContent>
                                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="label-mono">Type</Label>
                            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                                <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11" data-testid="special-kind-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="press">Press / Demo</SelectItem>
                                    <SelectItem value="courtesy">Courtesy / Promotional</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="label-mono">Duration (days)</Label>
                            <Input
                                type="number"
                                min="1"
                                max="3650"
                                className="mt-2 bg-avx-bg border-avx-line h-11 font-mono"
                                value={form.days}
                                onChange={(e) => setForm({ ...form, days: e.target.value })}
                                data-testid="special-days-input"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="label-mono">Max activations</Label>
                        <Input
                            type="number"
                            min="1"
                            max="20"
                            className="mt-2 bg-avx-bg border-avx-line h-11 font-mono"
                            value={form.max_activations}
                            onChange={(e) => setForm({ ...form, max_activations: e.target.value })}
                            data-testid="special-max-activations"
                        />
                    </div>

                    <div>
                        <Label className="label-mono">Internal notes (optional)</Label>
                        <Textarea
                            rows={2}
                            className="mt-2 bg-avx-bg border-avx-line"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="e.g.: Sound On Sound reviewer · BF campaign"
                            data-testid="special-notes"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary w-full py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm disabled:opacity-60"
                        data-testid="special-submit-btn"
                    >
                        {submitting ? "Issuing…" : `Issue license · ${selectedProduct?.name || "…"}`}
                    </button>
                </div>
            </form>

            {/* LIST */}
            <div className="flex items-end justify-between mb-4">
                <div>
                    <div className="label-mono mb-2">/ Issued licenses</div>
                    <div className="text-avx-muted text-sm">{filtered.length} {filtered.length === 1 ? "license" : "licenses"}</div>
                </div>
                <div>
                    <Label className="label-mono">Filter by type</Label>
                    <Select value={kindFilter} onValueChange={setKindFilter}>
                        <SelectTrigger className="mt-2 w-48 bg-avx-bg border-avx-line h-10" data-testid="special-filter-kind">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="press">Press / Demo</SelectItem>
                            <SelectItem value="courtesy">Courtesy</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="space-y-3">
                {filtered.length === 0 ? (
                    <div className="panel p-10 text-center text-avx-muted" data-testid="special-empty">
                        No special licenses have been issued yet.
                    </div>
                ) : filtered.map((lic) => {
                    const meta = KIND_META[lic.kind] || KIND_META.courtesy;
                    const active = lic.status === "active" && !lic.is_expired;
                    return (
                        <div key={lic.id} className="panel-elevated p-5" data-testid={`special-license-${lic.id}`}>
                            <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
                                <div>
                                    <div className="flex items-center gap-3 flex-wrap mb-2">
                                        <span className="font-mono font-bold text-base break-all">{lic.key}</span>
                                        <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-sm border ${meta.pill}`}>
                                            {meta.label}
                                        </span>
                                        <StatusBadge status={active ? "active" : "cancelled"} />
                                        {lic.is_expired && (
                                            <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-sm bg-avx-danger/15 text-avx-danger border border-avx-danger/40">
                                                Expirada
                                            </span>
                                        )}
                                        <span className="label-mono">{lic.active_count}/{lic.max_activations} machines</span>
                                    </div>
                                    <div className="text-sm text-avx-muted">
                                        <span className="text-avx-text">{lic.user?.name || "—"}</span>
                                        <span className="mx-1">·</span>
                                        <span className="font-mono text-xs">{lic.user?.email || ""}</span>
                                        <span className="mx-1">·</span>
                                        <span className="text-avx-text">{lic.product?.name || "—"}</span>
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-3 mt-3 text-xs font-mono">
                                        <div>
                                            <div className="label-mono">Issued</div>
                                            <div>{formatDateTime(lic.created_at)}</div>
                                        </div>
                                        <div>
                                            <div className="label-mono">Expires</div>
                                            <div>{lic.expires_at ? formatDateTime(lic.expires_at) : "—"}</div>
                                        </div>
                                        <div>
                                            <div className="label-mono">Days remaining</div>
                                            <div className={lic.days_remaining <= 3 && lic.days_remaining >= 0 ? "text-avx-blue" : ""}>
                                                {lic.days_remaining ?? "—"}
                                            </div>
                                        </div>
                                    </div>
                                    {lic.internal_notes && (
                                        <div className="mt-3 text-xs text-avx-muted italic bg-avx-bg/60 border border-avx-line rounded-sm px-3 py-2">
                                            {lic.internal_notes}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2 min-w-[140px]">
                                    <button
                                        onClick={() => copyKey(lic.key)}
                                        className="btn-secondary text-[10px] font-mono uppercase tracking-[0.18em] py-2 rounded-sm"
                                        data-testid={`special-copy-${lic.id}`}
                                    >
                                        {copied === lic.key ? "Copied ✓" : "Copy key"}
                                    </button>
                                    <button
                                        onClick={() => extend(lic)}
                                        className="text-[10px] font-mono uppercase tracking-[0.18em] py-2 rounded-sm border border-avx-blue/40 text-avx-blue hover:bg-avx-blue/10"
                                        data-testid={`special-extend-${lic.id}`}
                                    >
                                        + Extend
                                    </button>
                                    {active && (
                                        <button
                                            onClick={() => revoke(lic)}
                                            className="text-[10px] font-mono uppercase tracking-[0.18em] py-2 rounded-sm border border-avx-danger/30 text-avx-danger hover:bg-avx-danger/10"
                                            data-testid={`special-revoke-${lic.id}`}
                                        >
                                            Revocar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </DashboardLayout>
    );
}
